import { useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'next/router';

import { useFeatureFlag } from '~/src/common/services/FeatureFlag';
import { Category } from '~/src/common/typings/category';
import { formatBatchDiscountLabel, formatPromoLabel } from '~/src/common/utils/prices';
import {
  SPECIFIC_TAG_ATTRIBUTE_KEY,
  getProductPromoWithBulkDiscount,
} from '~/src/common/utils/product';
import { getGetCategoryQueryKey } from '~/src/queries/api-ecom/generated/api-ecom';
import { AllowedProductForCard } from '~/src/typings/products/types';

import * as S from './layout';

interface Props {
  product: AllowedProductForCard;
  cartQuantity: number;
  freeQuantity: number;
}

const ProductTags = ({ product, cartQuantity, freeQuantity }: Props) => {
  const queryClient = useQueryClient();
  const isNewTagActivated = useFeatureFlag('badge-nouveau-produit');
  const { query } = useRouter<{ categorySlug: string }>();
  const { itemPrice, attributes } = product;

  const promo = getProductPromoWithBulkDiscount(product);

  const hasPromo = promo != null;
  const isBatchDiscount = hasPromo && promo.mechanism === 'BATCH_DISCOUNT';

  const specificTag = attributes.find(({ key }) => key === SPECIFIC_TAG_ATTRIBUTE_KEY)?.value as
    | string
    | undefined;

  if (!hasPromo && !specificTag) return null;

  // Temporaire : L'information ne devrait pas être gérée via un tag manuel
  const isNewTag = specificTag?.toLowerCase().trim() === 'nouveau';
  // Le seul endroit où le tag doit être masqué c'est sur la page catégorie
  // par conséquent, categorySlug doit être défini et la donnée dans le cache
  const shouldHideNewTag =
    !isNewTagActivated ||
    Boolean(
      queryClient
        .getQueryData<Category>(getGetCategoryQueryKey(query.categorySlug))
        ?.tags?.includes('nouveautes'),
    );

  return (
    <S.TagsContainer>
      {hasPromo ? (
        <S.PromoTag $backgroundColor="PROMOTION" $color="BLACK">
          {isBatchDiscount
            ? formatBatchDiscountLabel(promo, cartQuantity - freeQuantity)
            : formatPromoLabel(promo, itemPrice)}
        </S.PromoTag>
      ) : null}

      {isNewTag && !shouldHideNewTag ? (
        <S.Tag $backgroundColor="PURPLE" $color="WHITE">
          {specificTag}
        </S.Tag>
      ) : null}

      {specificTag && !isNewTag ? (
        <S.Tag $backgroundColor="LIGHT_GREEN" $color="PRIMARY">
          {specificTag}
        </S.Tag>
      ) : null}
    </S.TagsContainer>
  );
};

export default ProductTags;
